import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { landingPathForRole } from "@/lib/permissions";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import ControlModes from "@/pages/ControlModes";
import GateTemplates from "@/pages/GateTemplates";
import Logistics from "@/pages/Logistics";
import FieldIntel from "@/pages/FieldIntel";
import ConceptIntake from "@/pages/ConceptIntake";
import AdminConsole from "@/pages/AdminConsole";
import Orders from "@/pages/Orders";
import RegisterViaInvite from "@/pages/RegisterViaInvite";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";

function hasClientSessionMarker() {
    if (typeof window === "undefined") return false;
    try {
        if (window.localStorage && window.localStorage.getItem("kitt_session") === "1") return true;
        // Legacy fallback during/after the Cerberus → KITT rebrand.
        if (window.localStorage && window.localStorage.getItem("cerberus_session") === "1") return true;
    } catch {
        /* ignore */
    }
    if (typeof document !== "undefined" && document.cookie) {
        if (/(?:^|;\s*)kitt_session=1/.test(document.cookie)) return true;
        if (/(?:^|;\s*)cerberus_session=1/.test(document.cookie)) return true;
    }
    return false;
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    // While we're still probing /auth/me — OR a probe hasn't started yet — hold render.
    // The session marker is only consulted DURING this loading window to keep a freshly
    // post-login page from briefly flashing /login while AuthContext catches up.
    if (loading || user === null) {
        // If we definitely have no marker, don't make the user wait — go straight to /login.
        if (!loading && user === null && !hasClientSessionMarker()) {
            return <Navigate to="/login" replace />;
        }
        return (
            <div className="min-h-screen bg-jet text-snow-dim flex items-center justify-center font-mono text-sm">
                <div className="overline animate-pulse-signal">Authorizing…</div>
            </div>
        );
    }
    // /auth/me has resolved. If it said "no user", redirect — the marker is meaningless
    // once the server has spoken. (A stale, non-httpOnly cookie marker can otherwise
    // trap users on broken pages where every request 401s.)
    if (user === false) {
        console.warn("[AUTH] ProtectedRoute redirecting: /auth/me returned no user.");
        return <Navigate to="/login" replace />;
    }
    return (
        <ErrorBoundary label="KITT shell crashed" testId="layout-error-boundary">
            <Layout>
                <ErrorBoundary label="Page render failed" testId="page-error-boundary">
                    {children}
                </ErrorBoundary>
            </Layout>
        </ErrorBoundary>
    );
}

function RootRedirect() {
    // Build Prompt #14: route the user to their landing page based on role.
    const { user, loading } = useAuth();
    if (loading || user === null) {
        return (
            <div className="min-h-screen bg-jet text-snow-dim flex items-center justify-center font-mono text-sm">
                <div className="overline animate-pulse-signal">Routing…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/login" replace />;
    return <Navigate to={landingPathForRole(user.role)} replace />;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
            <Route path="/products/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
            <Route path="/logistics" element={<ProtectedRoute><Logistics /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
            <Route path="/field-intel" element={<ProtectedRoute><FieldIntel /></ProtectedRoute>} />
            <Route path="/concept-intake" element={<ProtectedRoute><ConceptIntake /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminConsole /></ProtectedRoute>} />
            <Route path="/register" element={<RegisterViaInvite />} />
            <Route path="/control-modes" element={<ProtectedRoute><ControlModes /></ProtectedRoute>} />
            <Route path="/gate-templates" element={<ProtectedRoute><GateTemplates /></ProtectedRoute>} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="app-shell grain">
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                    <Toaster
                        theme="dark"
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "#1A1A1A",
                                color: "#F5F5F5",
                                border: "1px solid #333333",
                                borderRadius: "0",
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: "12px",
                            },
                        }}
                    />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
